import { useEffect, Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { useAuthStore } from "@/store/authStore";

import { StorefrontLayout } from "@/components/StorefrontLayout";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { LoginSlideOver } from "@/components/LoginSlideOver";
import { ChatBot } from "@/components/ChatBot";   // ← new

const HomePage = lazy(() => import("@/pages/HomePage"));
const ShopPage = lazy(() => import("@/pages/ShopPage"));
const ProductDetailPage = lazy(() => import("@/pages/ProductDetailPage"));
const CheckoutPage = lazy(() => import("@/pages/CheckoutPage"));
const CheckoutSuccessPage = lazy(() => import("@/pages/CheckoutSuccessPage"));
const DashboardPromoBannersPage = lazy(() => import("@/pages/dashboard/DashboardPromoBannersPage"));

const LoginPage = lazy(() => import("@/pages/account/LoginPage"));
const RegisterPage = lazy(() => import("@/pages/account/RegisterPage"));
const RegisterAgentPage = lazy(() => import("@/pages/account/RegisterAgentPage"));
const MyOrdersPage = lazy(() => import("@/pages/account/MyOrdersPage"));
const OrderDetailPage = lazy(() => import("@/pages/account/OrderDetailPage"));
const WishlistPage = lazy(() => import("@/pages/account/WishlistPage"));

const AboutPage = lazy(() => import("@/pages/StaticPages").then((m) => ({ default: m.AboutPage })));
const ContactPage = lazy(() => import("@/pages/StaticPages").then((m) => ({ default: m.ContactPage })));
const ShippingPage = lazy(() => import("@/pages/StaticPages").then((m) => ({ default: m.ShippingPage })));
const ReturnsPage = lazy(() => import("@/pages/StaticPages").then((m) => ({ default: m.ReturnsPage })));
const FaqsPage = lazy(() => import("@/pages/StaticPages").then((m) => ({ default: m.FaqsPage })));
const PrivacyPage = lazy(() => import("@/pages/StaticPages").then((m) => ({ default: m.PrivacyPage })));

const DashboardLayout = lazy(() => import("@/pages/dashboard/DashboardLayout"));
const DashboardOverviewPage = lazy(() => import("@/pages/dashboard/DashboardOverviewPage"));
const DashboardOrdersPage = lazy(() => import("@/pages/dashboard/DashboardOrdersPage"));
const DashboardProductsPage = lazy(() => import("@/pages/dashboard/DashboardProductsPage"));
const DashboardBannersPage = lazy(() => import("@/pages/dashboard/DashboardBannersPage"));
const DashboardBrandsPage = lazy(() => import("@/pages/dashboard/DashboardBrandsPage"));
const DashboardTopCategoriesPage = lazy(() => import("@/pages/dashboard/DashboardTopCategoriesPage"));
const DashboardCustomersPage = lazy(() => import("@/pages/dashboard/DashboardCustomersPage"));
const DashboardSettingsPage = lazy(() => import("@/pages/dashboard/DashboardSettingsPage"));

function PageFallback() {
  return <div className="container-page py-20 text-center text-steel-600">Loading…</div>;
}

export default function App() {
  const initialize = useAuthStore((s) => s.initialize);

  useEffect(() => {
    initialize();
  }, [initialize]);

  return (
    <BrowserRouter>
      <LoginSlideOver />
      <ChatBot />                          {/* ← floating chat button */}
      <Suspense fallback={<PageFallback />}>
        <Routes>
          <Route element={<StorefrontLayout />}>
            <Route index element={<HomePage />} />
            <Route path="shop" element={<ShopPage />} />
            <Route path="products/:slug" element={<ProductDetailPage />} />

            <Route path="account/login" element={<LoginPage />} />
            <Route path="account/register" element={<RegisterPage />} />
            <Route path="account/register-agent" element={<RegisterAgentPage />} />

            <Route path="about" element={<AboutPage />} />
            <Route path="contact" element={<ContactPage />} />
            <Route path="shipping" element={<ShippingPage />} />
            <Route path="returns" element={<ReturnsPage />} />
            <Route path="faqs" element={<FaqsPage />} />
            <Route path="privacy" element={<PrivacyPage />} />

            <Route element={<ProtectedRoute />}>
              <Route path="checkout" element={<CheckoutPage />} />
              <Route path="checkout/success" element={<CheckoutSuccessPage />} />
              <Route path="account/orders" element={<MyOrdersPage />} />
              <Route path="account/orders/:id" element={<OrderDetailPage />} />
              <Route path="account/wishlist" element={<WishlistPage />} />
            </Route>
          </Route>

          <Route element={<ProtectedRoute requireRole="AGENT" />}>
            <Route path="dashboard" element={<DashboardLayout />}>
              <Route index element={<DashboardOverviewPage />} />
              <Route path="orders" element={<DashboardOrdersPage />} />
              <Route path="products" element={<DashboardProductsPage />} />
              <Route path="banners" element={<DashboardBannersPage />} />
              <Route path="brands" element={<DashboardBrandsPage />} />
              <Route path="top-categories" element={<DashboardTopCategoriesPage />} />
              <Route path="customers" element={<DashboardCustomersPage />} />
              <Route path="settings" element={<DashboardSettingsPage />} />
              <Route path="promo-banners" element={<DashboardPromoBannersPage />} />
            </Route>
          </Route>
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}